<template>
  <div
    v-if="totalRecents > 0"
    class="sidebar-recents">
    <div class="sidebar-recents__header">
      <div class="sidebar-recents__title">
        <span>
          <span class="sidebar-recents__count">{{ totalRecents }}</span>
          {{ t('crudmanager.sidebar.recents') }}
        </span>
        <button
          v-if="totalDrafts > 0"
          type="button"
          class="sidebar-recents__drafts"
          :title="t('crudmanager.sidebar.drafts')"
          @click="openDrafts">
          ·
          <span class="sidebar-recents__drafts-count">{{ totalDrafts }}</span>
          {{ t('crudmanager.sidebar.drafts') }}
        </button>
      </div>
      <div class="sidebar-recents__actions">
        <button
          type="button"
          class="sidebar-recents__see-all"
          @click="openAllRecents">
          {{ t('crudmanager.sidebar.seeAll') }}
          <q-icon :name="symRoundedChevronRight" />
        </button>
      </div>
    </div>

    <div class="sidebar-recents__list">
      <button
        v-for="instance in topRecents"
        :key="instance.id"
        type="button"
        :class="['recent-card', { 'is-active': isInstanceActive(instance) }]"
        @click="openInstance(instance)">
        <div
          class="recent-card__icon-wrap"
          :class="{ 'is-draft': isDraft(instance) }">
          <q-icon
            :name="modelIcon(instance) || symRoundedInbox"
            class="recent-card__icon" />
        </div>
        <div class="recent-card__body">
          <div class="recent-card__title">
            {{ instanceTitle(instance) }}
          </div>
          <div class="recent-card__meta">
            <span
              class="recent-card__type-badge"
              :class="{ 'is-draft': isDraft(instance) }">
              {{ modelLabel(instance) }}<template v-if="isDraft(instance)">&nbsp;{{ t('crudmanager.sidebar.draft') }}</template>
            </span>
            <span class="recent-card__time">{{ relativeTime(instance.openedAt as unknown as string | null) }}</span>
          </div>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { symRoundedChevronRight, symRoundedInbox } from '@quasar/extras/material-symbols-rounded'
import { Dialog } from 'quasar'
import useCrudUtils from 'src/CrudManager/Core/composables/CrudUtils/useCrudUtils'
import useDate from 'src/CrudManager/Core/composables/useDate'
import { useAppSettingsStore } from 'src/CrudManager/Core/stores/useAppSettingsStore'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import type { CrudBadgeSubtitle, CrudInstance } from 'src/CrudManager/Core/types/AdvancedCrudManager'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import AllRecentsDialog from './AllRecentsDialog.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const crudManagerStore = useCrudManagerStore()
const appSettingsStore = useAppSettingsStore()
const { generateModuleUrl } = useCrudUtils()
const { getRelativeTime } = useDate()

const isInstanceActive = (instance: CrudInstance) => {
  if (!crudManagerStore.isInitiated) return false
  const url = generateModuleUrl(instance.modelName, instance.moduleName, instance.props?.modelId as string)
  return !!url && route.path === url
}

const isRecent = (i: CrudInstance) =>
  !!i.openedAt && (i.moduleName === 'editor' || i.moduleName === 'creator' || i.moduleName === 'external')

const allRecents = computed(() =>
  crudManagerStore.crudInstances
    .filter(isRecent)
    .slice()
    .sort((a, b) => new Date(b.openedAt || 0).getTime() - new Date(a.openedAt || 0).getTime()),
)

const totalRecents = computed(() => allRecents.value.length)
const topRecents = computed(() => {
  const count = Math.min(Math.max(appSettingsStore.sidebarSettings?.recentsCount ?? 3, 1), 10)
  return allRecents.value.slice(0, count)
})

const isDraft = (i: CrudInstance) => i.moduleName === 'creator' && i.status?.userTouched === true

const totalDrafts = computed(() => allRecents.value.filter(isDraft).length)

const subtitleText = (instance: CrudInstance): string => {
  const sub = instance.subtitle
  if (sub && typeof sub === 'object' && typeof (sub as CrudBadgeSubtitle)?.text === 'string') {
    return (sub as CrudBadgeSubtitle).text || ''
  }
  if (typeof sub === 'string') return sub
  return ''
}

// Title follows what the model declares via getTitle / getSubtitle. For
// drafts we prefer the live subtitle (model identifier like "Bon de travail
// #22026") over the static getTitle output ("Créer une nouvelle transaction").
const instanceTitle = (instance: CrudInstance) => {
  if (isDraft(instance)) return subtitleText(instance) || instance.title || modelLabel(instance)
  return instance.title || modelLabel(instance)
}

const modelLabel = (instance: CrudInstance) => t(`crudmodels.${instance.modelName}.name`, 1)

const modelIcon = (instance: CrudInstance) => {
  if (!crudManagerStore.isInitiated) return undefined
  return crudManagerStore.getModelMeta(instance.modelName)?.icon
}

const relativeTime = (iso: string | null) => {
  if (!iso) return ''
  return getRelativeTime(iso)
}

const openInstance = (instance: CrudInstance) => {
  if (!crudManagerStore.isInitiated) return
  const url = generateModuleUrl(instance.modelName, instance.moduleName, instance.props?.modelId as string)
  if (url) router.push(url)
}

const openAllRecents = () => {
  Dialog.create({
    component: AllRecentsDialog,
  })
}

const openDrafts = () => {
  Dialog.create({
    component: AllRecentsDialog,
    componentProps: {
      initialPeriod: 'draft',
    },
  })
}
</script>

<style lang="scss" scoped>
.sidebar-recents {
  padding: 10px 14px 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.body--light .sidebar-recents {
  border-top-color: rgba(0, 0, 0, 0.06);
}

.sidebar-recents__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 2px 8px;
}

.sidebar-recents__title {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: $grey-5;
  opacity: 0.75;
}

.sidebar-recents__count {
  margin-left: 6px;
  font-weight: 600;
  opacity: 0.7;
}

.sidebar-recents__actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.sidebar-recents__see-all {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: none;
  background: transparent;
  color: var(--q-primary);
  font-size: 0.72rem;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background-color: color-mix(in srgb, var(--q-primary) 8%, transparent);
  }

  .q-icon {
    font-size: 0.95rem;
  }
}

.sidebar-recents__drafts {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  border: none;
  background: transparent;
  color: var(--q-warning, #f59e0b);
  font: inherit;
  font-size: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;
  padding: 0;
  margin-left: 4px;
  transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    opacity: 0.85;
  }
}

.sidebar-recents__drafts-count {
  font-weight: 700;
  opacity: 0.95;
}

.sidebar-recents__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.recent-card {
  display: grid;
  grid-template-columns: 28px 1fr;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &.is-active {
    background: color-mix(in srgb, var(--q-primary) 22%, transparent);

    .recent-card__title {
      color: #ffffff;
      font-weight: 600;
    }
  }
}

.body--light {
  .recent-card:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .recent-card.is-active {
    background: color-mix(in srgb, var(--q-primary) 18%, transparent);

    .recent-card__title {
      color: var(--q-primary);
    }
  }
}

.recent-card__icon-wrap {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}

.body--light .recent-card__icon-wrap {
  background: rgba(0, 0, 0, 0.05);
}

.recent-card__icon-wrap.is-draft {
  background: color-mix(in srgb, var(--q-warning, #f59e0b) 18%, transparent);
  color: var(--q-warning, #f59e0b);
}

.recent-card__icon {
  font-size: 0.95rem;
  opacity: 0.85;
}

.recent-card__icon-wrap.is-draft .recent-card__icon {
  color: var(--q-warning, #f59e0b);
  opacity: 1;
}

.recent-card__body {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.recent-card__title {
  font-size: 0.78rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-card__meta {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.65rem;
  color: $grey-6;
  min-width: 0;
}

.recent-card__type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.08);
  color: inherit;
  opacity: 0.85;
  line-height: 1.5;
  white-space: nowrap;
  flex-shrink: 0;
}

.body--light .recent-card__type-badge {
  background: rgba(0, 0, 0, 0.05);
}

.recent-card__type-badge.is-draft {
  background: color-mix(in srgb, var(--q-warning, #f59e0b) 20%, transparent);
  color: var(--q-warning, #f59e0b);
  opacity: 1;
}

.recent-card__time {
  opacity: 0.85;
  white-space: nowrap;
  margin-left: auto;
}
</style>
